import React, {Suspense} from "react";
import {Route, Switch} from "react-router-dom";
import lazyWithRetry from "./libs/lazyWithRetry";
import AuthenticatedRoute from "./components/AuthenticatedRoute";
import UnauthenticatedRoute from "./components/UnauthenticatedRoute";
import AsyncAppliedRoute from "./components/AppliedRoute"
import {Redirect, generatePath} from "react-router";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";

const AsyncAllIntegrationsContainer = lazyWithRetry(() => import("./containers/Admin/ProvisionCompany/AllIntegrationsContainer"));

const AsyncHome = lazyWithRetry(() => import("./containers/Home/Home"));
const AsyncNotFound = lazyWithRetry(() => import("./containers/NotFound"));
const AsyncLogin = lazyWithRetry(() => import("./containers/LoginScreen"));
const AsyncSignup = lazyWithRetry(() => import("./containers/Signup/Signup"));
const AsyncAccountView = lazyWithRetry(() => import("./containers/Accounts/AccountView"));
const AsyncRules = lazyWithRetry(() => import("./containers/Rules/Rules"));
const AsyncResetPassword = lazyWithRetry(() => import("./containers/ResetPassword"));
const AsyncSettings = lazyWithRetry(() => import("./containers/Settings"));
const AsyncDesktopAuthLink = lazyWithRetry(() => import("./containers/DesktopAuthLink"));
const AsyncDesktopAuthComplete = lazyWithRetry(() => import("./containers/DesktopAuthComplete"));
const AsyncDesktopToolsHub = lazyWithRetry(() => import("./containers/DesktopToolsHub"));
const AsyncAuthenticatedBillingRedux = lazyWithRetry(() => import("./containers/Billing/AuthenticatedBillingRedux"));
const AsyncSubscriptionChangeSuccessRedux = lazyWithRetry(() => import("./containers/Billing/SubscriptionChangeSuccessRedux"));
const AsyncUnauthenticatedBillingRedux = lazyWithRetry(() => import("./containers/Billing/UnauthenticatedBillingRedux"));
const AsyncUpgradeContainerRedux = lazyWithRetry(() => import("./containers/Billing/UpgradeContainerRedux"));
const AsyncChangePassword = lazyWithRetry(() => import("./containers/ChangePassword"));
const AsyncChangeEmail = lazyWithRetry(() => import("./containers/ChangeEmail"));
const AsyncInboundResponse = lazyWithRetry(() => import("./components/Diagrams/InboundResponseData"));
const AsyncSalesForecast = lazyWithRetry(() => import("./components/Diagrams/SalesForecastData"));
const AsyncDashboard = lazyWithRetry(() => import("./components/Diagrams/DashboardData"));
const AsyncScenarioToSheetsConfig = lazyWithRetry(() => import("./containers/ScenarioToSheetsConfig/ScenarioToSheetsConfig"));
const AsyncScenarioSettings = lazyWithRetry(() => import("./containers/Dashboard/ScenarioSettings"));
const AsyncScenarioWinLossSetup = lazyWithRetry(() => import("./containers/WinLossReasons/WinLossSetupV2"));
const AsyncWinLossReasonsView = lazyWithRetry(() => import("./containers/WinLossReasons/WinLossReasonsView"));
const AsyncPipelineAttribution = lazyWithRetry(() => import("./components/Diagrams/PipelineAttribution"));
const AsyncPConvert = lazyWithRetry(() => import("./components/Diagrams/PConvert"));
// const AsyncPClose = lazyWithRetry(() => import("./components/Diagrams/PClose"));
const AsyncPClosev2 = lazyWithRetry(() => import("./components/Diagrams/PClosev2"));
const AsyncAccountManagement = lazyWithRetry(() => import("./containers/Accounts/AccountManagement"));
const AsyncRulesData = lazyWithRetry(() => import("./components/Diagrams/RulesData"));
const AsyncProgramEffectivenessData = lazyWithRetry(() => import("./components/Diagrams/ProgramEffectivenessData"));
const AsyncSalesEffortData = lazyWithRetry(() => import("./components/Diagrams/SalesEffortData"));
const AsyncConversionsView = lazyWithRetry(() => import("./containers/Conversions/ConversionsView"));
const AsyncGongDataExplorer = lazyWithRetry(() => import("./containers/SalesEffort/GongDataExplorer"));
const AsyncSalesloftDataExplorer = lazyWithRetry(() => import("./containers/SalesEffort/SalesloftDataExplorer"));
const AsyncHubSpotDataExplorer = lazyWithRetry(() => import("./containers/SalesEffort/HubSpotDataExplorer"));
const AsyncMeetingsPage = lazyWithRetry(() => import("./containers/Meetings/MeetingsPage"));
const AsyncSalesRampData = lazyWithRetry(() => import("./components/Diagrams/SalesRampData"));
const AsyncSalesWinRateTrendsData = lazyWithRetry(() => import("./components/Diagrams/SalesWinRateTrendsData"));
const AsyncFAQContainer = lazyWithRetry(() => import("./containers/FAQs/FAQContainer"));
const AsyncUsersData = lazyWithRetry(() => import("./components/Diagrams/UsersData"));
const AsyncNewUser = lazyWithRetry(() => import("./containers/Company/NewUser"));
const AsyncUpdateUser = lazyWithRetry(() => import("./containers/Company/UpdateUser"));
const AsyncCompanySettingsPage = lazyWithRetry(() => import("./containers/Company/CompanySettings"));
const AsyncAccessDenied = lazyWithRetry(() => import("./containers/AccessDenied"));
const AsyncAcceptInvite = lazyWithRetry(() => import("./containers/Company/AcceptInvite"));
const AsyncVerifyEmail = lazyWithRetry(() => import("./containers/VerifyEmail"));
const AsyncCompanyProvisionStepper = lazyWithRetry(() => import("./containers/Admin/ProvisionCompany/CompanyProvisionStepper"));
const AsyncCompanyConfigureDataStepper = lazyWithRetry(() => import("./containers/Admin/ConfigureCompanyData/CompanyConfigureDataStepper"));
const AsyncRouteManagement = lazyWithRetry(() => import("./containers/Admin/RouteManagement"));
const AsyncTermsContainer = lazyWithRetry(() => import("./containers/ServiceTerms/TermsContainer"));
const AsyncSecurityContainer = lazyWithRetry(() => import("./containers/Security/SecurityPrivacyArchitectureContainer"));
const AsyncSalesProductivityData = lazyWithRetry(() => import("./components/Diagrams/SalesProductivityData"));
const AsyncMarketingProductPage = lazyWithRetry(() => import("./containers/MarketingPages/MarketingProduct"));
const AsyncMarketingAttributionPage = lazyWithRetry(() => import("./containers/MarketingPages/MarketingAttribution"));
const AsyncMarketingCampaignsPage = lazyWithRetry(() => import("./containers/MarketingPages/MarketingCampaigns"));
const AsyncMarketingSalesRampPage = lazyWithRetry(() => import("./containers/MarketingPages/MarketingSalesRamp"));
const AsyncMarketingSalesEffortPage = lazyWithRetry(() => import("./containers/MarketingPages/MarketingSalesEffort"));
const AsyncMarketingSalesForecastsPage = lazyWithRetry(() => import("./containers/MarketingPages/MarketingSalesForecasts"));
const AsyncMarketingSalesProductivityPage = lazyWithRetry(() => import("./containers/MarketingPages/MarketingSalesProductivity"));
const AsyncMarketingMLRecipesPage = lazyWithRetry(() => import("./containers/MarketingPages/MarketingMLRecipes"));
const AsyncMarketingContactUs = lazyWithRetry(() => import("./containers/MarketingPages/ContactUs/ContactUsContainer"));
const AsyncMarketingInboundFollowUp = lazyWithRetry(() => import("./containers/MarketingPages/MarketingInboundFollowUp.js"));
const AsyncMarketingSolutionsSales = lazyWithRetry(() => import("./containers/MarketingPages/MarketingSolutionSales.js"));
const AsyncMarketingSolutionsMarketing = lazyWithRetry(() => import("./containers/MarketingPages/MarketingSolutionMarketing.js"));
const AsyncMarketingSolutionRevOps = lazyWithRetry(() => import("./containers/MarketingPages/MarketingSolutionRevOps.js"));
const AsyncMarketingSolutionFinance = lazyWithRetry(() => import("./containers/MarketingPages/MarketingSolutionFinance.js"));
const AsyncSalesForecastSetup = lazyWithRetry(() => import("./containers/SalesForecast/SalesForecastSetup"));
const AsyncInboundResponseSetup = lazyWithRetry(() => import("./containers/InboundResponse/InboundResponseSetup"));
const AsyncSalesQuotaSetup = lazyWithRetry(() => import("./containers/SalesForecast/SalesQuotaSetupContainer"));
const AsyncSalesArrSetup = lazyWithRetry(() => import("./containers/ArrSchedule/ArrScheduleDisplay"));
const AsyncTaskLabelSetup = lazyWithRetry(() => import("./containers/TaskLabelConfig/TaskLabelSetup"));
const AsyncSamlRedirect = lazyWithRetry(() => import("./containers/SamlRedirect"));
const AsyncMarketingPartners = lazyWithRetry(() => import("./containers/MarketingPages/MarketingPartners"));
const AsyncMarketingCaseStudyActon = lazyWithRetry(() => import("./containers/MarketingPages/CaseStudies/CaseStudyActOn"));
const AsyncMarketingPartnerArcTech = lazyWithRetry(() => import("./containers/MarketingPages/PartnerLandingPages/PartnerArcTechnologies"));
const AsyncMarketingPartnerLevelUpFinance = lazyWithRetry(() => import("./containers/MarketingPages/PartnerLandingPages/PartnerLevelUpFinance"));
const AsyncPartnerCliffSimon = lazyWithRetry(() => import("./containers/MarketingPages/PartnerLandingPages/PartnerCliffSimon"));
const AsyncPartnerRevOpsAf = lazyWithRetry(() => import("./containers/MarketingPages/PartnerLandingPages/PartnerRevOpsAf"));
// const AsyncPartnerRevOpsAfMeet = lazyWithRetry(() => import("./containers/MarketingPages/PartnerLandingPages/PartnerRevOpsAfMeet"));
const AsyncPartnerFutureFinance = lazyWithRetry(() => import("./containers/MarketingPages/PartnerLandingPages/PartnerFutureFinance"));
const AsyncAttributionAdvancedSetup = lazyWithRetry(() => import("./containers/Attribution/AttributionAdvancedSetup"));
const AsyncMetaManager = lazyWithRetry(() => import("./containers/MetaManager/MetaManager"));
const AsyncGtmStatsContainer = lazyWithRetry(() => import("./containers/GtmStats/GtmStatsContainer"));
const AsyncBoardStatsContainer = lazyWithRetry(() => import("./containers/BoardStats/BoardStatsContainer"));
const AsyncGtmStatsSegmentManagerContainer = lazyWithRetry(() => import("./containers/GtmStats/GtmStatsSegmentManager"));
const AsyncRevenueDefinitionSegmentManagerContainer = lazyWithRetry(() => import("./containers/CustomerInvoices/RevenueDefinitionSegmentManager"));
const AsyncRevenueForecastingControlCenter = lazyWithRetry(() => import("./containers/RevenueForecasting/RevenueForecastingControlCenter"));
const AsyncRevenueInvoiceProjection = lazyWithRetry(() => import("./containers/BoardStats/Revenue/RevenueInvoiceProjectionChart"));
const AsyncRevenueForecastingControlCenterOld = lazyWithRetry(() => import("./containers/RevenueForecasting/RevenueForecastingControlCenterOld"));
const AsyncExecutiveSummary = lazyWithRetry(() => import("./containers/ExecSummary/ExecSummaryHeadless"));
const AsyncExecutiveSummaryTemp = lazyWithRetry(() => import("./containers/ExecSummary/ExecSummaryTemp"));
const AsyncHygieneContainer = lazyWithRetry(() => import("./containers/Hygiene/HygieneContainer"));
const AsyncRenewalMatching = lazyWithRetry(() => import("./containers/RenewalMatching/RenewalMatching"));
const AsyncOppCreationLegacyRedirect = lazyWithRetry(() => import("./containers/OppCreationStats/OppCreationLegacyRedirect"));
const AsyncPreOppConfig = lazyWithRetry(() => import("./containers/ProgramEffectiveness/PreOppConfig"));
const AsyncMarketingBusinessDigest = lazyWithRetry(() => import("./containers/MarketingPages/MarketingBusinessDigest"));
const AsyncMarketingGtmStats = lazyWithRetry(() => import("./containers/MarketingPages/MarketingGtmStats"));
const AsyncMarketingMultiQuarterProjections = lazyWithRetry(() => import("./containers/MarketingPages/MarketingMultiQuarterProjections"));
const AsyncMarketingLeadership = lazyWithRetry(() => import("./containers/MarketingPages/MarketingLeadershipPage"));
const AsyncHubSpotPublicDocumentationContainer = lazyWithRetry(() => import("./containers/PublicDocumentation/HubSpotPublicDocumentationContainer"));
const AsyncGongPublicDocumentationContainer = lazyWithRetry(() => import("./containers/PublicDocumentation/GongPublicDocumentationContainer"));
const AsyncNetsuitePublicDocumentationContainer = lazyWithRetry(() => import("./containers/PublicDocumentation/NetSuitePublicDocumentationContainer"));
const AsyncCustomerInvoices = lazyWithRetry(() => import("./containers/CustomerInvoices/CustomerInvoices"));
const AsyncDepartmentSpend = lazyWithRetry(() => import("./containers/DepartmentSpend/DepartmentSpend"));
const AsyncDepartmentSetup = lazyWithRetry(() => import("./containers/DepartmentSpend/DepartmentSetup"));
const AsyncPromptManager = lazyWithRetry(() => import("./containers/PromptManager/PromptManagerContainer"));
const AsyncRecipeDesigner = lazyWithRetry(() => import("./containers/RecipeDesigner/RecipeDesigner"));
const AsyncRecipeResults = lazyWithRetry(() => import("./containers/RecipeResults/RecipeResults"));
const AsyncConnectSlack = lazyWithRetry(() => import("./containers/Admin/ConnectSlack"));
const AsyncConnectGong = lazyWithRetry(() => import("./containers/Admin/ConnectGong"));
const AsyncTrialEndedSplash = lazyWithRetry(() => import("./containers/TrialEnded/TrialEndedSplash"));
const AsyncCareersPage = lazyWithRetry(() => import("./containers/Careers/CareersPage"));
const AsyncGtmFinanceDinnerSeptember2024 = lazyWithRetry(() => import("./containers/MarketingPages/EventLandingPages/GtmFinanceDinnerSeptember2024"));
const AsyncGoalSetsManager = lazyWithRetry(() => import("./containers/GoalSets/GoalSetsManager"));
const AsyncMatchModule = lazyWithRetry(() => import("./containers/MatchModule/MatchModuleContainer"));
const AsyncEntityDetailView = lazyWithRetry(() => import("./containers/MatchModule/EntityDetailView"));
const AsyncMatchingJobDetail = lazyWithRetry(() => import("./containers/MatchingJobDetail/MatchingJobDetailContainer"));
const AsyncMatchingJobResults = lazyWithRetry(() => import("./containers/MatchingJobResults/MatchingJobResultsContainer"));
const AsyncGrowthPlanningPage = lazyWithRetry(() => import("./containers/MarketingPages/GrowthPlanningPage"));
const AsyncFixTheDataStoryPage = lazyWithRetry(() => import("./containers/MarketingPages/StoryPageFixTheData"));
const AsyncNextGenDataAssemblyStoryPage = lazyWithRetry(() => import("./containers/MarketingPages/MarketingProductDataAssembly"));
const AsyncMarketingOngoingHygieneAndOneTimeCleanup = lazyWithRetry(() => import("./containers/MarketingPages/MarketingOngoingHygieneAndOneTimeCleanup"));
const AsyncMarketinMultiCrm = lazyWithRetry(() => import("./containers/MarketingPages/MarketingMultiCrmUseCase"));
const AsyncPlanTheGrowthStoryPage = lazyWithRetry(() => import("./containers/MarketingPages/StoryPagePlanTheGrowth"));
const AsyncRunTheBusinessStoryPage = lazyWithRetry(() => import("./containers/MarketingPages/StoryPageRunTheBusiness"));
const AsyncTellTheStoryStoryPage = lazyWithRetry(() => import("./containers/MarketingPages/StoryPageTellTheStory"));
const AsyncAutomaticDriversLayer = lazyWithRetry(() => import("./containers/MarketingPages/AutomaticDriversLayer"));
const AsyncWinLossStageFalloffRecipe = lazyWithRetry(() => import("./containers/MarketingPages/WinLossStageFalloffRecipe"));

const NEW_BUSINESS_FUNNEL_ROUTE = "/new-business-funnel/:custv?/:enforceDType?/:summarizeBy?/:dopt?";
const LEGACY_PCONVERT_ROUTE = "/pconvert/:tab?/:custv?/:enforceDType?/:summarizeBy?/:dopt?";
const OPEN_PIPELINE_ROUTE = "/open-pipeline/:tab?";
const LEGACY_PCLOSE_V2_ROUTE = "/pclose-v2/:tab?";
const LEGACY_PCLOSE_ROUTE = "/pclose/:tab?/:sourceScenario?/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?/:averageDate?/:averageSize?/:enforceDType?";
const WIN_ATTRIBUTION_ROUTE = "/winflows/:ttype?/:arr?/:dType?/:view?/:cfseg?/:custv?/:dopt?";
const PIPELINE_ATTRIBUTION_ROUTE = "/pipelineflows/:ttype?/:arr?/:dType?/:view?/:cfseg?/:custv?/:dopt?";

export default function Routes({appProps}) {

    function RedirectWithStatus({from, to, status}) {
        return (
            <Route
                render={({staticContext}) => {
                    // there is no `staticContext` on the client, so
                    // we need to guard against that here
                    if (staticContext) staticContext.status = status;
                    return <Redirect from={from} to={to}/>;
                }}
            />
        );
    }

    function LegacyPConvertRedirect({match, location}) {
        const {custv, enforceDType, summarizeBy, dopt} = match.params;
        const pathname = generatePath(NEW_BUSINESS_FUNNEL_ROUTE, {
            custv,
            enforceDType,
            summarizeBy,
            dopt,
        });

        return <Redirect to={`${pathname}${location.search || ""}${location.hash || ""}`}/>;
    }

    function LegacyPCloseRedirect({match, location}) {
        const pathname = generatePath(OPEN_PIPELINE_ROUTE, {
            tab: match.params.tab,
        });

        return <Redirect to={`${pathname}${location.search || ""}${location.hash || ""}`}/>;
    }

    function LegacyWinAttributionRedirect({match, location}) {
        const pathname = generatePath(PIPELINE_ATTRIBUTION_ROUTE, match.params);

        return <Redirect to={`${pathname}${location.search || ""}${location.hash || ""}`}/>;
    }


    return (

        // <Suspense fallback={<div style={{paddingTop: '5em'}}>loading...</div>}>
        <Switch>


            <RedirectWithStatus status={301} from="/sales-effort"
                                to="/gtm-activity"/>

            <RedirectWithStatus status={301} from="/products/machine-learning"
                                to="/products/qpilot"/>
            <RedirectWithStatus status={301} from="/revopsaf/meet"
                                to="/revopsaf"/>
            <RedirectWithStatus status={301} from="/sign-up"
                                to="/signup"/>
            <RedirectWithStatus status={301} exact from="/products"
                                to="/products/integrations"/>
            <RedirectWithStatus status={301} from="/products/inbound-lead-response"
                                to="/products/inbound-lead-response-measurement"/>
            <RedirectWithStatus status={301} exact from="/products"
                                to="/products/integrations"/>
            <RedirectWithStatus status={301} from="/products/sales-productivity"
                                to="/products/sales-pipeline-analytics"/>
            <RedirectWithStatus status={301} from="/products/sales-activity-analytics" to="/products/gtm-activity-analytics"/>
            <RedirectWithStatus status={301} from="/products/sales-activity" to="/products/gtm-activity-analytics"/>
            <RedirectWithStatus status={301} from="/products/sales-forecasting" to="/products/forecasting"/>
            <RedirectWithStatus status={301} from="/products/sales-pipeline-analytics-analytics"
                                to="/products/sales-pipeline-analytics"/>
            <RedirectWithStatus status={301} from="/products/attribution" to="/products/account-based-attribution"/>
            <RedirectWithStatus status={301} from="/service-terms" to="/terms/service-terms"/>
            <RedirectWithStatus status={301} from="/privacy-policy" to="/terms/privacy-policy"/>
            <RedirectWithStatus status={301} from="/products/sales-productivity-analytics"
                                to="/products/sales-pipeline-analytics"/>

            <Suspense fallback={<Container sx={{height: "80vh"}}
                                           maxWidth={"xl"}>
                <Box/>
            </Container>}>
                <AsyncAppliedRoute path="/" exact component={AsyncHome}  />
                <UnauthenticatedRoute path="/signup/:competitorRenewalCampaign?/:step?/:signupEmail?/:codeWasSent?"
                                      exact
                                      component={AsyncSignup}
                                       />
                <UnauthenticatedRoute path="/about" exact component={AsyncMarketingLeadership}
                                      />
                <UnauthenticatedRoute path="/login" exact component={AsyncLogin}
                                      />
                <UnauthenticatedRoute path="/login/reset" exact component={AsyncResetPassword}
                                      />
                <UnauthenticatedRoute path="/login/set-password/:invitedEmail?/:codeWasSent?" exact
                                      component={AsyncAcceptInvite}  />
                <UnauthenticatedRoute path="/verify-email" exact component={AsyncVerifyEmail}
                                      />
                <UnauthenticatedRoute path="/products/integrations" exact component={AsyncMarketingProductPage}

                                      />
                <UnauthenticatedRoute path="/products/account-based-attribution" exact
                                      component={AsyncMarketingAttributionPage}  />
                <UnauthenticatedRoute path="/products/campaign-measurement" exact
                                      component={AsyncMarketingCampaignsPage}
                                       />
                <UnauthenticatedRoute path="/products/gtm-activity-analytics" exact
                                      component={AsyncMarketingSalesEffortPage}  />
                <UnauthenticatedRoute path="/products/sales-pipeline-analytics" exact
                                      component={AsyncMarketingSalesProductivityPage}
                                      />
                <UnauthenticatedRoute path="/products/rep-ramp" exact component={AsyncMarketingSalesRampPage}
                                       />
                <UnauthenticatedRoute path="/products/forecasting" exact component={AsyncMarketingSalesForecastsPage}
                                       />
                <UnauthenticatedRoute path="/products/growth-planning-a" exact component={AsyncGrowthPlanningPage}
                                       />
                <UnauthenticatedRoute path="/use-cases/plan-the-growth" exact component={AsyncPlanTheGrowthStoryPage}
                                       />
                                       <UnauthenticatedRoute path="/use-cases/run-the-business" exact component={AsyncRunTheBusinessStoryPage}
                                       />
                                       <UnauthenticatedRoute path="/use-cases/tell-the-story" exact component={AsyncTellTheStoryStoryPage}
                                       />
                                       <UnauthenticatedRoute path="/recipes/win-loss-stage-falloff" exact component={AsyncWinLossStageFalloffRecipe}
                                       />
                                       <UnauthenticatedRoute path="/products/performance-drivers" exact component={AsyncAutomaticDriversLayer}
                                       />
                <UnauthenticatedRoute path="/products/business-digest" exact component={AsyncMarketingBusinessDigest}
                                       />
                <UnauthenticatedRoute path="/products/go-to-market-stats" exact component={AsyncMarketingGtmStats}
                                       />
                <UnauthenticatedRoute path="/products/multi-quarter-projections" exact
                                      component={AsyncMarketingMultiQuarterProjections}
                                       />

                <UnauthenticatedRoute path="/products/qpilot" exact component={AsyncMarketingMLRecipesPage}
                                       />
                <UnauthenticatedRoute path="/products/inbound-lead-response-measurement" exact
                                      component={AsyncMarketingInboundFollowUp}  />
                <UnauthenticatedRoute path="/solutions/sales" exact component={AsyncMarketingSolutionsSales}
                                       />
                <UnauthenticatedRoute path="/solutions/finance" exact
                                      component={AsyncMarketingSolutionFinance}
                                       />
                <UnauthenticatedRoute path="/solutions/revenue-operations" exact
                                      component={AsyncMarketingSolutionRevOps}
                                       />

                <UnauthenticatedRoute path="/solutions/revenue-teams" exact
                                      component={AsyncMarketingSolutionRevOps}
                                       />


                <UnauthenticatedRoute path="/solutions/marketing" exact component={AsyncMarketingSolutionsMarketing}
                                       />
                <UnauthenticatedRoute path="/partners" exact component={AsyncMarketingPartners}
                                       />

                <UnauthenticatedRoute path="/case-studies/act-on" exact component={AsyncMarketingCaseStudyActon}
                                       />

                <UnauthenticatedRoute path="/partners/arc" exact component={AsyncMarketingPartnerArcTech}
                                       />

                <UnauthenticatedRoute path="/partners/levelup-finance" exact component={AsyncMarketingPartnerLevelUpFinance}
                                       />


                <UnauthenticatedRoute path="/partners/cliff-simon" exact component={AsyncPartnerCliffSimon}
                                       />
                <UnauthenticatedRoute path="/revopsaf" exact component={AsyncPartnerRevOpsAf}
                                       />

                <UnauthenticatedRoute path="/future-finance" exact component={AsyncPartnerFutureFinance}
                                       />

                <UnauthenticatedRoute path="/pricing" exact component={AsyncUnauthenticatedBillingRedux}
                                      />

                <Route path="/contact-us/:repName?" exact component={AsyncMarketingContactUs}
                       />
                <Route path="/security" exact component={AsyncSecurityContainer}
                       />

                <Route path="/public-docs/hubspot-integration" exact
                       component={AsyncHubSpotPublicDocumentationContainer}
                       />

                <Route path="/public-docs/gong-integration" exact
                       component={AsyncGongPublicDocumentationContainer}
                       />

                <Route path="/public-docs/netsuite-integration" exact
                       component={AsyncNetsuitePublicDocumentationContainer}
                       />

                <Route path="/desktop/auth-complete" exact component={AsyncDesktopAuthComplete}
                       />

                <Route path="/terms/:tab?" exact component={AsyncTermsContainer}
                       />

                <AuthenticatedRoute path="/settings/password" exact component={AsyncChangePassword}
                                    />
                <AuthenticatedRoute path="/desktop-link" exact component={AsyncDesktopAuthLink}
                                    />
                <AuthenticatedRoute path="/desktop-tools/:tab?/:skillId?" exact component={AsyncDesktopToolsHub}
                                    />
                <AuthenticatedRoute path="/access-denied/:reason?" exact component={AsyncAccessDenied}

                                    />
                <AuthenticatedRoute path={"/trial-ended"} exact component={AsyncTrialEndedSplash}

                                    />
                <AuthenticatedRoute path="/settings/email" exact component={AsyncChangeEmail}
                                    />
                <AuthenticatedRoute path="/settings" exact component={AsyncSettings}
                                    />
                <AuthenticatedRoute path="/admin/billing" exact component={AsyncAuthenticatedBillingRedux}
                                     />
                <AuthenticatedRoute path="/admin/billing/checkout/:sPlan?/:sPid?/:sPrice?" exact
                                    component={AsyncUpgradeContainerRedux}  />
                <AuthenticatedRoute path="/admin/billing/success" exact
                                    component={AsyncSubscriptionChangeSuccessRedux}
                                     />
                <AuthenticatedRoute path="/config/program-detail/:id" exact component={AsyncRules}
                                    />
                <AuthenticatedRoute
                    path="/config/programs/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?"
                    exact component={AsyncRulesData}  />
                <Route path={WIN_ATTRIBUTION_ROUTE} exact component={LegacyWinAttributionRedirect}  />
                <AuthenticatedRoute
                    path={PIPELINE_ATTRIBUTION_ROUTE}
                    exact
                    component={AsyncPipelineAttribution}  />
                <AuthenticatedRoute path={NEW_BUSINESS_FUNNEL_ROUTE} exact component={AsyncPConvert}
                                    permissionSlug="pconvert"
                                    />
                <Route path={LEGACY_PCONVERT_ROUTE} exact component={LegacyPConvertRedirect}
                                    />
                <AuthenticatedRoute
                    path="/account-management/:tab?/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?"
                    exact component={AsyncAccountManagement}  />

                <AuthenticatedRoute
                    path={OPEN_PIPELINE_ROUTE}
                    exact component={AsyncPClosev2}
                    permissionSlug="pclose"  />
                <Route path={LEGACY_PCLOSE_V2_ROUTE} exact component={LegacyPCloseRedirect}  />
                <Route path={LEGACY_PCLOSE_ROUTE} exact component={LegacyPCloseRedirect}  />
                <AuthenticatedRoute path="/attribution-setup/:actStep?/:showAdvanced?" exact
                                    component={AsyncAttributionAdvancedSetup}
                                     />


                <AuthenticatedRoute
                    path="/program-effectiveness/:log?/:ss?/:isLink?/:tab?/:cf?/:custv?/:dopt?/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?"
                    exact component={AsyncProgramEffectivenessData}  />
                <AuthenticatedRoute path="/gtm-activity/:tab?/:ttype?/:ms?/:ss?/:dopt?/:sids?/:snames?" exact
                                    component={AsyncSalesEffortData}  />
                <AuthenticatedRoute path="/conversions" exact
                                    component={AsyncConversionsView}  />
                <AuthenticatedRoute path="/meetings" exact
                                    component={AsyncMeetingsPage}  />
                <AuthenticatedRoute path="/gong-explorer" exact
                                    component={AsyncGongDataExplorer}  />
                <AuthenticatedRoute path="/salesloft-explorer" exact
                                    component={AsyncSalesloftDataExplorer}  />
                <AuthenticatedRoute path="/hubspot-explorer" exact
                                    component={AsyncHubSpotDataExplorer}  />
                <AuthenticatedRoute path="/rep-ramp/:tab?/:ttype?/:dopt?/:ms?/:ss?/:sids?/:snames?/:rids?/:rnames?"
                                    exact
                                    component={AsyncSalesRampData}  />
                {/*<AuthenticatedRoute*/}
                {/*    path="/account-management/:tab?/:qGroups?/:ss?/:ti?/:dType?/:ts?/:sids?/:snames?/:isLink?/:dopt?/:ncds?/:ncde?/:pdd?/:custv?/:refQuarter?/:anchDate?/:repId?/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?"*/}
                {/*    exact*/}
                {/*    component={AsyncAccountManagement}  />*/}
                <AuthenticatedRoute
                    // path="/pipeline-forensics/:tab?/:qGroups?/:ss?/:ti?/:dType?/:ts?/:sids?/:snames?/:isLink?/:dopt?/:ncds?/:ncde?/:pdd?/:custv?/:refQuarter?/:anchDate?"
                    path="/pipeline-forensics/:tab?/:qGroups?/:ss?/:ti?/:dType?/:ts?/:sids?/:snames?/:isLink?/:dopt?/:ncds?/:ncde?/:pdd?/:custv?/:refQuarter?/:anchDate?/:repId?/:pcs?/:pce?/:goalSetId?/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?"
                    exact
                    component={AsyncSalesProductivityData}  />
                <AuthenticatedRoute
                    path="/sales-productivity"
                    component={({match}) => (
                        <Redirect
                            to={`/pipeline-forensics/`}/>
                    )}/>

                <UnauthenticatedRoute path="/events/gtm-finance-dinner-san-francisco-sept-2024"
                                    exact
                                    component={AsyncGtmFinanceDinnerSeptember2024}  />


                <AuthenticatedRoute path="/win-rate/:ti?/:ss?/:dType?/:dateType?/:custv?/:inclOpen?/:enforceDType?/:dopt?/:qGroups?/:indIds?/:comparisonMode?"
                                    exact
                                    component={AsyncSalesWinRateTrendsData}  />
                <AuthenticatedRoute path="/faq/:faqSlugs?" exact component={AsyncFAQContainer}
                                    />
                <AuthenticatedRoute path="/manage-users/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?" exact component={AsyncUsersData}
                                    />

                <AuthenticatedRoute path="/company/new-user" exact component={AsyncNewUser}
                                    />
                <AuthenticatedRoute path="/company/update-user/:userid?" exact component={AsyncUpdateUser}
                                     />
                <AuthenticatedRoute path="/admin/company/create/:actstep?/:platform?" exact
                                    component={AsyncCompanyProvisionStepper}
                                    roles={['admin_user']}  />
                <AuthenticatedRoute path="/admin/company/settings" exact
                                    component={AsyncCompanySettingsPage}  />
                <AuthenticatedRoute path="/admin/company/configure-data/:actstep?/:showAdvanced?" exact
                                    component={AsyncCompanyConfigureDataStepper}
                                    />

                <AuthenticatedRoute path="/admin/company/integrations" exact
                                    component={AsyncAllIntegrationsContainer}
                                    />

                <AuthenticatedRoute path="/admin/routes" exact
                                    component={AsyncRouteManagement}
                                    roles={['admin_user']}
                                    />

                <AuthenticatedRoute path="/inbound-follow-up/:ss?/:ttype?/:custv?/:sids?/:dopt?" exact
                                    component={AsyncInboundResponse}  />

                <AuthenticatedRoute
                    path="/forecast/:tab?/:period?/:isLink?/:qGroups?/:scId?/:dType?/:selGoalSetId?/:isoB?/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?/:dealRollupViewToSet?/:dealRollupWrapperSettingsToSet?"

                    exact component={AsyncSalesForecast}  />

<AuthenticatedRoute
                    path="/win-loss-setup"
                    exact component={AsyncScenarioWinLossSetup}  />
                    
                <AuthenticatedRoute
                    path="/win-loss-reasons"
                    exact component={AsyncWinLossReasonsView}  />

                <AuthenticatedRoute
                    path="/scenario-sync"
                    exact component={AsyncScenarioToSheetsConfig}  />
                <AuthenticatedRoute
                    path="/scenario-settings"
                    exact component={AsyncScenarioSettings}  />
                <AuthenticatedRoute
                    path="/scenarios/:tab?/:viewMode?/:scId?/:quarterGroup?/:averageSize?/:averageDate?/:goalOpt?/:quotaOpt?/:plugOpt?/:isoSegment?/:dateFilter?"
                    exact component={AsyncDashboard}  />
                <AuthenticatedRoute path="/forecast-setup/:actStep?/:showExplainer?" exact
                                    component={AsyncSalesForecastSetup}
                                     />
                <AuthenticatedRoute path="/inbound-response-setup/:actStep?" exact component={AsyncInboundResponseSetup}
                                     />
                <AuthenticatedRoute
                    path="/quota-setup/:actStep?/:actQtr?/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?"
                    exact component={AsyncSalesQuotaSetup}  />

                <AuthenticatedRoute
                    path="/arr-schedule/:dopt?/:custv?/:arrSource?"
                    exact component={AsyncSalesArrSetup}  />

                <AuthenticatedRoute
                    path="/customer-invoices/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?"
                    exact component={AsyncCustomerInvoices}  />

                <AuthenticatedRoute
                    path="/department-spend/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?"
                    exact component={AsyncDepartmentSpend}  />

                <AuthenticatedRoute
                    path="/department-definitions/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?"
                    exact component={AsyncDepartmentSetup}  />

                <AuthenticatedRoute
                    path="/manage-prompts/:qSearchTextToSet?/:qRowsPerPageToSet?/:qColumnOrderToSet?/:qColsFiltered?/:qColsVisibleToSet?/:qSortOrderToSet?"
                    exact component={AsyncPromptManager}  />

                <AuthenticatedRoute path="/recipe-designer/:recipe_id?/:execution_id?" exact component={AsyncRecipeDesigner} />

                <AuthenticatedRoute path="/recipe-results/:recipe_id?/:execution_id?" component={AsyncRecipeResults} />


                                <AuthenticatedRoute
                    path="/connect-gong"
                    exact component={AsyncConnectGong}  />

                                <AuthenticatedRoute
                    path="/connect-slack"
                    exact component={AsyncConnectSlack}  />

                <AuthenticatedRoute path="/manage-task-labels" exact component={AsyncTaskLabelSetup}
                                    />
                <AuthenticatedRoute path="/metadata-setup" exact component={AsyncMetaManager}
                                    />
                <AuthenticatedRoute path="/config/pre-opp" exact component={AsyncPreOppConfig}
                                    />

                <AuthenticatedRoute path="/gtm-stats/:tab?/:fsChartSegmentId?/:fsChartColorId?/:custv?/:enforceDType?/:showAvg?/:groupQtr?/:scId?/:dopt?/:totOnBar?/:tot?/:attn?/:pfocus?"
                                    exact
                                    component={AsyncGtmStatsContainer}
                                    />

                <AuthenticatedRoute path="/goal-sets/:goalSetId?"
                                    exact
                                    component={AsyncGoalSetsManager}
                                    />

                <AuthenticatedRoute path="/gtm-stats-segments" exact
                                    component={AsyncGtmStatsSegmentManagerContainer}
                                    />


                <AuthenticatedRoute path="/revenue-definitions" exact component={AsyncRevenueDefinitionSegmentManagerContainer}

                                    />

                <AuthenticatedRoute path="/revenue-forecasting" exact component={AsyncRevenueForecastingControlCenter}

                                    />

                {process.env.REACT_APP_STAGE !== "prod" &&
                    <AuthenticatedRoute path="/revenue-invoice-projection" exact component={AsyncRevenueInvoiceProjection}

                                        />
                }

                {process.env.REACT_APP_STAGE !== "prod" &&
                    <AuthenticatedRoute path="/revenue-forecasting-old" exact component={AsyncRevenueForecastingControlCenterOld}
                                        permissionSlug="revenue-forecasting"
                                        />
                }

                <AuthenticatedRoute path="/board-stats/:tab?/:custv?/:enforceDType?/:arrSource?/:dopt?" exact
                                    component={AsyncBoardStatsContainer}
                                    />

                <AuthenticatedRoute path="/opp-creation/:dType?/:goalOpt?/:custv?/:dopt?" exact
                                    component={AsyncOppCreationLegacyRedirect}
                                    />

                <AuthenticatedRoute path="/accounts/:selectedAccountId" exact component={AsyncAccountView}

                                    />
                <AuthenticatedRoute path="/business-digest/:tab?/:recordId?/:startD?/:endD?" exact
                                    component={AsyncExecutiveSummary}
                                    />
                <AuthenticatedRoute path="/business-digest-temp/:tab?/:recordId?/:startD?/:endD?" exact
                                    component={AsyncExecutiveSummaryTemp}
                                    />

                {/* Hygiene Routes */}
                {/* Single route with wildcard to handle all sub-paths */}
                <AuthenticatedRoute path="/hygiene/:tab?/:param1?/:param2?"
                    component={AsyncHygieneContainer}
                />

                {/* Renewal Matching Route */}
                <AuthenticatedRoute path="/renewal-matching"
                    component={AsyncRenewalMatching}
                />

                {/* Backward compatibility redirects */}
                <AuthenticatedRoute path="/business-digest/hygiene/:insightId?" exact
                    render={({ match }) => <Redirect to={`/hygiene/monitoring${match.params.insightId ? `/${match.params.insightId}` : ''}`} />}
                />
                                
                <UnauthenticatedRoute path="/identityredirect/:providerData?" exact component={AsyncSamlRedirect}
                                       />

                <UnauthenticatedRoute path="/careers" exact component={AsyncCareersPage}
                                       />

                {/* <AuthenticatedRoute
                    path="/entity-match/:page?/:rowsPerPage?/:search?/:sort?/:sortDirection?"
                    exact component={AsyncMatchModule}
                /> */}

                {/* <AuthenticatedRoute
                    path="/entity-match/:entityId"
                    exact component={AsyncEntityDetailView}
                /> */}

                <AuthenticatedRoute
                    path="/matching-jobs/:jobId"
                    exact component={AsyncMatchingJobDetail}
                />

                <AuthenticatedRoute
                    path="/matching-jobs/:jobId/results"
                    exact component={AsyncMatchingJobResults}
                />

                <UnauthenticatedRoute path="/use-cases/fix-the-data" exact component={AsyncFixTheDataStoryPage} />

                <UnauthenticatedRoute path="/products/next-gen-data-assembly" exact component={AsyncNextGenDataAssemblyStoryPage} />

                <UnauthenticatedRoute path="/products/data-hygiene" exact component={AsyncMarketingOngoingHygieneAndOneTimeCleanup} />

                <UnauthenticatedRoute path="/products/multiple-crms" exact component={AsyncMarketinMultiCrm} />

            </Suspense>
            <Route component={AsyncNotFound}/>
        </Switch>
        // </Suspense>
    )
}
